import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1dde86ed/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`General guidance`}</h2>
    <p><em parentName="p">{`Notifications`}</em>{` are messages that communicate information to the user.`}</p>
    <h2>{`Format`}</h2>
    <h4>{`Title`}</h4>
    <p>{`All notifications have subject titles, which should be short and descriptive. (Example: “Tester-app-02 has crashed.”)`}</p>
    <h4>{`Message`}</h4>
    <p>{`We recommend the body of the notification be contained within two lines. Be descriptive and include any troubleshooting actions or next steps. When possible, communicate the main message using just the title. You can include `}<a parentName="p" {...{
        "href": "/components/link/code"
      }}>{`links`}</a>{` within the notification body that redirect the user to next steps.`}</p>
    <h4>{`Dismissal`}</h4>
    <p>{`We recommend that toast notifications automatically disappear after five seconds. Inline notifications are persistent until the user dismisses them. All notifications have at least one method of dismissal (typically, it is a small “x” in the upper right hand corner).`}</p>
    <h4>{`Icons`}</h4>
    <p>{`Icons may provide additional clarity. Icons should be placed to the left of a title. These glyphs (16x16) can be found in the `}<a parentName="p" {...{
        "href": "/guidelines/icons/library"
      }}>{`icons`}</a>{` library.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "36.141304347826086%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAAAkElEQVQoz2NgwAOeaFkwUA3cdTYE0y9cjZj22+iyMDBwMK620AaLiYmJkW7gdUVLiMGKVsJHFcykGVgYmP/rJ5NnGAj83CYKpv/vFhV5ulxUloFBnMPLWoyFbBcGrPEG04EbfMW957krApmcKpbKrGQbyDc/DEzzLgxn45sdwgFiC0WYkh+GIKC4NBhDjJBhACPaHIvdmYUGAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "The three icon states",
            "title": "The three icon states",
            "src": "/static/e5a108195e4a219bf597139d8338c57e/3cbba/notification-usage-1.png",
            "srcSet": ["/static/e5a108195e4a219bf597139d8338c57e/7fc1e/notification-usage-1.png 288w", "/static/e5a108195e4a219bf597139d8338c57e/a5df1/notification-usage-1.png 576w", "/static/e5a108195e4a219bf597139d8338c57e/3cbba/notification-usage-1.png 1152w", "/static/e5a108195e4a219bf597139d8338c57e/392b1/notification-usage-1.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2>{`Variations`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Notification type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Purpose`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Toast notification`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Toasts are a non-modal, time-based window elements used to display short messages; they usually appear at the bottom of the screen and disappear after a few seconds.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Inline notification`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Inline notifications show up in task flows, to notify users of the status of an action. They usually appear at the top of the primary content area.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`High vs. low contrast`}</h3>
    <p>{`Use high-contrast style notifications for critical messaging. Low-contrast notifications are best used for supplemental messaging and are less visually disruptive to users. When in doubt, use the low contrast style.`}</p>
    <p>{`Inline and toast notifications can use different styles but you should never mix styles within the variations.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "51.08695652173913%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABs0lEQVQoz6VSyUrDUBRNqv0BwY3uLAiCA9JCElNa0yFWxIUgGhd+gR9QuvUD3EhcK06oKOJKXOnCARdWbUur0LRGbTo422jtwut9z5oPsA8O55z3wsl5A8PgEPk+pt7h9Xp/hdPRw1IOCd2cRxgR+8QhSZL8Pp9PcrlcktPptPgPxAuCIPE83y+KoozoIBlut5tlxE6ukRhhtH9pcHwYlLFxUCYUUBQFQqEQhSzLlv7zHo8HMACCwSBg4FatpZ0wbcjL7gH84zS2CyMiuBjBlhTEBwIBqv1+P2WO4yL4fRh5GjFU2znLvJbfbJtL6w31nuHq8kqjaZo2Jp1O241i3j7TMsX+N8zFNLFGKW/XMMuanNtbaF072undTR50FcB0GPDuyECRIg0FRxZKVBPW0McrGYK2RDXbHqtmmq2gErzRS9k29hc1eIDoQwqiz1dw/nINOjzCLcWTpfWaL0AZcvAKnwAQM7UNkhEvY0P0NmIOns+U+Ic2f3h3Nnt4H1WPcufqSf5SPTEu1NNi/NejPkY+LcTUVFVXk1/6bPLrZj5RyUySjEQlS7OYb4C6H3aqolP+AShvAm37psPpAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "High vs. low contrast",
            "title": "High vs. low contrast",
            "src": "/static/21f59c6167af99e1c57c27bb8560c5dd/3cbba/notification-usage-4.png",
            "srcSet": ["/static/21f59c6167af99e1c57c27bb8560c5dd/7fc1e/notification-usage-4.png 288w", "/static/21f59c6167af99e1c57c27bb8560c5dd/a5df1/notification-usage-4.png 576w", "/static/21f59c6167af99e1c57c27bb8560c5dd/3cbba/notification-usage-4.png 1152w", "/static/21f59c6167af99e1c57c27bb8560c5dd/392b1/notification-usage-4.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2>{`Placement`}</h2>
    <h4>{`Toast notifications`}</h4>
    <p>{`Toast notifications slide in and out a page from the top-right corner. Actionable notifications do not appear on mobile screen widths.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "48.233695652173914%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAACJ0lEQVQoz4WRy4uSURjGrVUiEZEX1ERF3Q0VMZp4mRHps1qIKIIxbcZNi+iv6C9oN1SLooEJKlq0KrttU5nBaIayQR3z84K3z+to3+fn0zkHktlIB34873nPOc9533MUer0eOp3uv2i1WqjV6gUajYYpXTMajbLJZILBYEgoVCpVknCbsLGEO2eUysRFjea+w2Z7bLFYtihms3nLarUyHA7HI7vd/pAUZ1YAeEvYJuws4QXh+f7nLx8SHu8gEol0Y7GYEI/HhXA4LESj0TbHcaLf7/+ooINshiRJWDZkWWb642saHqcTHBcCd+MWQoT1QBBe3zr8awG43e7d8+fOnmaGw+FwQpBOMhgMGL1eT5LkmZR+n5rZbLa5z+OchwKuedC3Or++5pwHvVclr+sSMbz2S+9wnWKG5KBIwElGoxHG4zGIIfr9PvL7B9h+9gT3HuzgcvI13Hdf4Urypbyy+QYrG0+FVd/NTdZyuVxGPp8XS6USCoUCisUinSOTySCbzSKXy7F8iewrFg6RPeDxbreL1F6HaGf26fsIqVzvJ6mL+SlIeyCmYrVaRaPRYNCYXnB0dMSo1Wpo1OvEuIh69Td67SqEJg+hxcvHgxaNC9/20hcWn9JqtURBEFibFNpip9NBt9tFu91mbdM8nffpkwxH/5D/iBLVw/HxRMkMJ5MJKpWK1Gw2F+9HzXieR51URZWa0jytlJrSmF5KkKfTKdUSyXvIOetfm5Agla4qGDcAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Toast notification example",
            "title": "Toast notification example",
            "src": "/static/0c404a3fc5c061a9ec56177d6f64b210/3cbba/notification-usage-2.png",
            "srcSet": ["/static/0c404a3fc5c061a9ec56177d6f64b210/7fc1e/notification-usage-2.png 288w", "/static/0c404a3fc5c061a9ec56177d6f64b210/a5df1/notification-usage-2.png 576w", "/static/0c404a3fc5c061a9ec56177d6f64b210/3cbba/notification-usage-2.png 1152w", "/static/0c404a3fc5c061a9ec56177d6f64b210/392b1/notification-usage-2.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h4>{`Inline notifications`}</h4>
    <p>{`Inline notifications appear near its related item. In `}<a parentName="p" {...{
        "href": "/components/form/code"
      }}>{`forms`}</a>{`, we recommend placing the inline notification at the bottom of the form, right before the submission buttons. Depending on the context of the page, inline notifications can appear above the content as well.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "113.45108695652175%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAXCAYAAAALHW+jAAAACXBIWXMAAAsSAAALEgHS3X78AAADlElEQVQ4y62V32/bVBTHb4PcdWNquihNH+j6MrYh2vJDVebEa9M0VYGnadIeQAKJrSlC4sc7SAsP/Ads1RYmsfUBCTEoFRuCqmJSQRUaUIq6Mm3d6v5wnDqxHV//SpbQOHfHN61WlHRTJY700ffKN+fcc+45dhDaZj6fD9Wz1tZWFAgE0K4sHA5TDYVCDSzL7o/FYvsikcjTQ0NDe4vFoqdQKHhM0/RYluWxbZsCa+oD6yfGPwA0b9L0uB/m8/n6GzzPU02lUo2Li4sDqqqGSqVSDxCE7ELguB0W4IADOwbVdb3B1Uql0iZJ0iqYLAiClAGDUjOwnzEMY4t1o2qvuj6gT9XNkhCCdms7+kBWVGVZbhFF8Yt0On3FRdO0C3D556GsUbh8l/PAOeAS0L3ZFE9NQEib6sbGhldRlM/hgDEIftlVuM8xKHkMY7zFFeAr4GXXB9TzuCoadlMyJFB/A2saDeSUywEo9Tb0Ysm2rHvQkHrcBVLAoOvjzmdNQEGRq3eZtxv5tbVIRpb7sGn2ylquV87lqrq5VgzjuGpZEVD6aimbA/4fm/1slOpvHyf2/D0+MSBM/xpem/qZwzf/4PTfZ4E/q8zOcdrUDU6euNan/vATDaiOf18bUJ26QUu2vh5vy8zfElIL/6jpO3dFA1pupteBtGiKgCSJ+r37gja/kMO3Fl5xfbS/5v6/ObQJqd9EWVHoRrFU8q0KwkRGUb6DV+6qZdnfwpxt5xvgKnAdCO44h7hU3Mpyv6CqH4mS9Ilp2wlsGAloTkIHsGkkVIwB7WxO1z8FjtDr0nFtlvrEtWqmY196C7/MjBpLy5fU+3xSX15JYn45mVtaTeIVMflvNgdoFwti9vIDQXqRViVmazMkMzfpKWR6xkfm5q+TrDJVVnOTRDcmCcaTJLsySRSgBM9K2o/ElqaJtR6iPvlMbcBnu7qoPtPevu95lj39Uk/PSGdXd/zw4UPxQ8+9EG88tRr3vE3i6HUnjt4gw+gUeRedJB3U6WSdxvT19tKHwWCwbTAWE+FLrUb6+7PhECuz3IB88DQvH/yQyB3vl+WOD5yMb8SxvWec11yflrhTOzbdnZ3Vw06cQH6/n9nb1MQcPXKUifZHmGg0xqBBnkEcYdBAmUHHHWbPWw7T/p5Dk/CPOPVnKhqN7noO/e9UnvgnRdccx4ECoWOI5aIo8CaPvCMEtQyXgQryAs1nqpk1Dz8K+hCG4ol1n159cAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Form example with inline notification",
            "title": "Form example with inline notification",
            "src": "/static/6bd41b8d57188190ee7207e5c4a5f45f/3cbba/notification-usage-3.png",
            "srcSet": ["/static/6bd41b8d57188190ee7207e5c4a5f45f/7fc1e/notification-usage-3.png 288w", "/static/6bd41b8d57188190ee7207e5c4a5f45f/a5df1/notification-usage-3.png 576w", "/static/6bd41b8d57188190ee7207e5c4a5f45f/3cbba/notification-usage-3.png 1152w", "/static/6bd41b8d57188190ee7207e5c4a5f45f/392b1/notification-usage-3.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      